<script>
import { GlCollapse, GlButton, GlAlert, GlDropdown, GlDropdownItem, GlSprintf } from '@gitlab/ui';
import eventHub from 'ee/security_dashboard/utils/event_hub';
import { __, s__, n__ } from '~/locale';
import toast from '~/vue_shared/plugins/global_toast';
import { VULNERABILITY_STATE_OBJECTS } from 'ee/vulnerabilities/constants';

export default {
  components: {
    GlCollapse,
    GlButton,
    GlAlert,
    GlDropdown,
    GlDropdownItem,
    GlSprintf,
  },
  inject: ['vulnerabilitiesQuery'],
  props: {
    selectedVulnerabilities: {
      type: Array,
      required: true,
    },
    visible: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      isSubmitting: false,
      updateErrorText: null,
      selectedStatus: null,
    };
  },
  computed: {
    statusDropdownText() {
      return this.selectedStatus?.dropdownText || this.$options.i18n.statusDropdownPlaceholder;
    },
  },
  methods: {
    updateStatus(status) {
      this.selectedStatus = status;
    },
    resetSelected() {
      this.$emit('cancel-selection');
    },
    handleSubmit() {
      this.isSubmitting = true;
      this.updateErrorText = null;
      let fulfilledCount = 0;
      const rejected = [];

      const promises = this.selectedVulnerabilities.map((vulnerability) => {
        return this.$apollo
          .mutate({
            mutation: this.selectedStatus.mutation,
            variables: { id: vulnerability.id, ...this.selectedStatus.payload },
            refetchQueries: [this.vulnerabilitiesQuery],
            awaitRefetchQueries: true,
          })
          .then(({ data }) => {
            const [queryName] = Object.keys(data);

            if (data[queryName].errors?.length > 0) {
              throw data[queryName].errors;
            }

            fulfilledCount += 1;
            this.$emit('vulnerability-updated', vulnerability.id);
          })
          .catch(() => {
            rejected.push(vulnerability.id.split('/').pop());
          });
      });

      return Promise.all(promises).then(() => {
        this.isSubmitting = false;

        if (fulfilledCount > 0) {
          toast(this.$options.i18n.statusChanged(this.selectedStatus.action, fulfilledCount));
          eventHub.$emit('vulnerabilities-updated', this);
        }

        if (rejected.length > 0) {
          this.updateErrorText = this.$options.i18n.vulnerabilitiesUpdateFailed(
            rejected.join(', '),
          );
        }
      });
    },
  },
  i18n: {
    cancel: __('Cancel'),
    selected: s__('SecurityReports|%{count} Selected'),
    statusDropdownPlaceholder: s__('SecurityReports|Set status'),
    changeStatus: s__('SecurityReports|Change status'),
    statusChanged: (status, count) =>
      ({
        confirm: n__(
          '%d vulnerability set to confirmed',
          '%d vulnerabilities set to confirmed',
          count,
        ),
        resolve: n__(
          '%d vulnerability set to resolved',
          '%d vulnerabilities set to resolved',
          count,
        ),
        dismiss: n__(
          '%d vulnerability set to dismissed',
          '%d vulnerabilities set to dismissed',
          count,
        ),
        revert: n__(
          '%d vulnerability set to needs triage',
          '%d vulnerabilities set to needs triage',
          count,
        ),
      }[status]),
    vulnerabilitiesUpdateFailed: (vulnIds) =>
      s__(`SecurityReports|Failed updating vulnerabilities with the following IDs: ${vulnIds}`),
  },
  VULNERABILITY_STATE_OBJECTS,
};
</script>

<template>
  <gl-collapse :visible="visible">
    <div class="card" :class="{ 'with-error': Boolean(updateErrorText) }">
      <gl-alert v-if="updateErrorText" variant="danger" :dismissible="false">
        {{ updateErrorText }}
      </gl-alert>

      <form class="card-body gl-display-flex gl-align-items-center" @submit.prevent="handleSubmit">
        <div>
          <gl-sprintf :message="$options.i18n.selected">
            <template #count>
              <span class="gl-font-weight-bold">{{ selectedVulnerabilities.length }}</span>
            </template>
          </gl-sprintf>
        </div>

        <gl-dropdown
          data-qa-selector="vulnerability_card_status_dropdown"
          :text="statusDropdownText"
          :disabled="isSubmitting"
          class="gl-ml-6 gl-mr-4 gl-pl-6 gl-border-l"
        >
          <gl-dropdown-item
            v-for="status in $options.VULNERABILITY_STATE_OBJECTS"
            :key="status.action"
            :data-qa-selector="'item_status_' + status.action"
            :data-testid="status.action"
            :is-checked="status === selectedStatus"
            is-check-item
            @click="updateStatus(status)"
          >
            <div class="gl-font-weight-bold">{{ status.dropdownText }}</div>
            <div>{{ status.dropdownDescription }}</div>
          </gl-dropdown-item>
        </gl-dropdown>

        <template v-if="selectedStatus">
          <gl-button
            type="button"
            class="gl-ml-auto gl-mr-4"
            :disabled="isSubmitting"
            @click="resetSelected"
          >
            {{ $options.i18n.cancel }}
          </gl-button>
          <gl-button
            data-qa-selector="change_status_button"
            type="submit"
            variant="confirm"
            :loading="isSubmitting"
          >
            {{ $options.i18n.changeStatus }}
          </gl-button>
        </template>
      </form>
    </div>
  </gl-collapse>
</template>
